import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Add, CheckCircle } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  fetchAttendance,
  createAttendance,
  fetchAttendanceEmployeesForSubmit,
  patchAttendance,
} from '../../../services/employee';
import CustomPagination from '../../../components/common/CustomPagination.js';

const Attendance = () => {
  const { t } = useTranslation(['common', 'employees']);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [filters, setFilters] = useState({
    employee: '',
    date: '',
    start_date: '',
    end_date: '',
    status: '',
    approval_status: ''
  });
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [employeesForSubmit, setEmployeesForSubmit] = useState([]);
  const [employeesForSubmitLoading, setEmployeesForSubmitLoading] = useState(false);
  const [form, setForm] = useState({
    employee: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    status: 'present',
    check_in_time: '',
    check_out_time: '',
    notes: ''
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 20,
    totalCount: 0
  });
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  const totalPages = Math.ceil(pagination.totalCount / pagination.rowsPerPage) || 1;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...filters,
        page: pagination.page + 1,
        page_size: pagination.rowsPerPage
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await fetchAttendance(params);
      
      if (response.results) {
        setAttendance(response.results);
        setPagination(prev => ({
          ...prev,
          totalCount: response.count || 0
        }));
      } else {
        setAttendance(Array.isArray(response) ? response : []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch attendance');
      setAttendance([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setPagination(prev => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }));
  };

  const handleCustomRowsInputChange = (value) => {
    setCustomRowsInput(value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput);
    if (value && value > 0 && value <= 1000) {
      setPagination(prev => ({ ...prev, rowsPerPage: value, page: 0 }));
      setCustomRowsActive(false);
      setCustomRowsInput('');
    } else {
      setCustomRowsInput('');
      setCustomRowsActive(false);
    }
  };

  const openAddModal = useCallback(async () => {
    setAddModalOpen(true);
    setEmployeesForSubmitLoading(true);
    try {
      const list = await fetchAttendanceEmployeesForSubmit();
      setEmployeesForSubmit(Array.isArray(list) ? list : []);
    } catch {
      setEmployeesForSubmit([]);
    } finally {
      setEmployeesForSubmitLoading(false);
    }
  }, []);

  const closeAddModal = () => {
    setAddModalOpen(false);
    setForm({
      employee: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      status: 'present',
      check_in_time: '',
      check_out_time: '',
      notes: ''
    });
  };

  const handleAddSubmit = async () => {
    if (!form.employee || !form.date) {
      setError(t('employees:attendance.employee') + ' & ' + t('employees:attendance.date') + ' required');
      return;
    }
    setSubmitLoading(true);
    setError(null);
    try {
      const payload = {
        employee: form.employee,
        date: form.date,
        status: form.status,
        notes: form.notes || undefined
      };
      if (form.check_in_time) payload.check_in_time = form.check_in_time;
      if (form.check_out_time) payload.check_out_time = form.check_out_time;
      await createAttendance(payload);
      closeAddModal();
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.employee?.[0] || err.message || 'Failed to add attendance');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setApprovingId(id);
    setError(null);
    try {
      await patchAttendance(id, { approval_status: 'approved' });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to approve');
    } finally {
      setApprovingId(null);
    }
  };

  const rowsWithSerial = useMemo(() => {
    return attendance.map((att, index) => ({
      ...att,
      sn: pagination.page * pagination.rowsPerPage + index + 1
    }));
  }, [attendance, pagination.page, pagination.rowsPerPage]);

  const columns = useMemo(() => [
    {
      field: 'sn',
      headerName: t('employees:attendance.columns.sn'),
      width: 80,
      sortable: false,
      filterable: false
    },
    {
      field: 'employee_name',
      headerName: t('employees:attendance.columns.employee'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => params.value || 'N/A'
    },
    {
      field: 'employee_id',
      headerName: t('employees:attendance.columns.employeeId'),
      flex: 1,
      minWidth: 120,
      sortable: false
    },
    {
      field: 'date',
      headerName: t('employees:attendance.columns.date'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          return format(new Date(params.value), 'MMM dd, yyyy');
        } catch {
          return 'N/A';
        }
      }
    },
    {
      field: 'check_in_time',
      headerName: t('employees:attendance.columns.checkIn'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          return format(new Date(params.value), 'HH:mm');
        } catch {
          return 'N/A';
        }
      }
    },
    {
      field: 'check_out_time',
      headerName: t('employees:attendance.columns.checkOut'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          return format(new Date(params.value), 'HH:mm');
        } catch {
          return 'N/A';
        }
      }
    },
    {
      field: 'status',
      headerName: t('employees:attendance.columns.status'),
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const status = params.value || 'present';
        const colorMap = {
          'present': 'success',
          'absent': 'error',
          'late': 'warning',
          'half_day': 'info',
          'on_leave': 'default'
        };
        const labelMap = {
          'present': t('employees:attendance.status.present'),
          'absent': t('employees:attendance.status.absent'),
          'late': t('employees:attendance.status.late'),
          'half_day': t('employees:attendance.status.halfDay'),
          'on_leave': t('employees:attendance.status.onLeave')
        };
        return (
          <Chip
            label={labelMap[status] || status}
            color={colorMap[status] || 'default'}
            size="small"
          />
        );
      }
    },
    {
      field: 'approval_status',
      headerName: t('employees:attendance.columns.approvalStatus'),
      width: 110,
      sortable: false,
      renderCell: (params) => {
        const status = params.value || 'approved';
        const label = status === 'pending' ? t('employees:attendance.approvalPending') : t('employees:attendance.approvalApproved');
        return (
          <Chip
            label={label}
            color={status === 'pending' ? 'warning' : 'success'}
            size="small"
          />
        );
      }
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const pending = params.row.approval_status === 'pending';
        const id = params.row.id;
        if (!pending) return null;
        return (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={approvingId === id ? <CircularProgress size={14} /> : <CheckCircle />}
            disabled={approvingId !== null}
            onClick={() => handleApprove(id)}
          >
            {t('employees:attendance.approve')}
          </Button>
        );
      }
    },
    {
      field: 'work_hours',
      headerName: t('employees:attendance.columns.workHours'),
      width: 100,
      sortable: false,
      renderCell: (params) => params.value ? `${params.value}h` : 'N/A'
    },
    {
      field: 'overtime_hours',
      headerName: t('employees:attendance.columns.overtime'),
      width: 100,
      sortable: false,
      renderCell: (params) => params.value ? `${params.value}h` : '0h'
    }
  ], [t]);

  if (loading && attendance.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 2 }}>
          {t('employees:attendance.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
          >
            {t('common:buttons.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openAddModal}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('employees:attendance.addAttendance')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label={t('employees:attendance.employee')}
                fullWidth
                value={filters.employee}
                onChange={(e) => handleFilterChange('employee', e.target.value)}
                placeholder={t('employees:attendance.employeePlaceholder')}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label={t('employees:attendance.date')}
                fullWidth
                type="date"
                value={filters.date}
                onChange={(e) => handleFilterChange('date', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label={t('employees:leaveRequests.startDate')}
                fullWidth
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label={t('employees:leaveRequests.endDate')}
                fullWidth
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                label={t('employees:status')}
                fullWidth
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{
                  shrink: true,
                }}
              >
                <option value="">{t('employees:all')}</option>
                <option value="present">{t('employees:attendance.status.present')}</option>
                <option value="absent">{t('employees:attendance.status.absent')}</option>
                <option value="late">{t('employees:attendance.status.late')}</option>
                <option value="half_day">{t('employees:attendance.status.halfDay')}</option>
                <option value="on_leave">{t('employees:attendance.status.onLeave')}</option>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                label={t('employees:attendance.approvalStatus')}
                fullWidth
                value={filters.approval_status}
                onChange={(e) => handleFilterChange('approval_status', e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{
                  shrink: true,
                }}
              >
                <option value="">{t('employees:all')}</option>
                <option value="pending">{t('employees:attendance.approvalPending')}</option>
                <option value="approved">{t('employees:attendance.approvalApproved')}</option>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520
          }}>
            <DataGrid
              rows={rowsWithSerial}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1200,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-row': {
                  marginBottom: '4px',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={pagination.page}
          totalPages={totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>

      <Dialog open={addModalOpen} onClose={closeAddModal} maxWidth="sm" fullWidth>
        <DialogTitle>{t('employees:attendance.addAttendance')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                select
                fullWidth
                label={t('employees:attendance.employee')}
                value={form.employee}
                onChange={(e) => setForm((f) => ({ ...f, employee: e.target.value }))}
                disabled={employeesForSubmitLoading}
                required
              >
                <MenuItem value="">—</MenuItem>
                {employeesForSubmit.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.full_name} ({emp.employee_id || emp.id})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                type="date"
                label={t('employees:attendance.date')}
                value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                select
                fullWidth
                label={t('employees:attendance.columns.status')}
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              >
                <MenuItem value="present">{t('employees:attendance.status.present')}</MenuItem>
                <MenuItem value="absent">{t('employees:attendance.status.absent')}</MenuItem>
                <MenuItem value="late">{t('employees:attendance.status.late')}</MenuItem>
                <MenuItem value="half_day">{t('employees:attendance.status.halfDay')}</MenuItem>
                <MenuItem value="on_leave">{t('employees:attendance.status.onLeave')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                type="time"
                label={t('employees:attendance.columns.checkIn')}
                value={form.check_in_time}
                onChange={(e) => setForm((f) => ({ ...f, check_in_time: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                type="time"
                label={t('employees:attendance.columns.checkOut')}
                value={form.check_out_time}
                onChange={(e) => setForm((f) => ({ ...f, check_out_time: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('employees:attendance.notes')}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeAddModal}>{t('common:buttons.cancel')}</Button>
          <Button variant="contained" onClick={handleAddSubmit} disabled={submitLoading} sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}>
            {submitLoading ? <CircularProgress size={24} /> : t('common:buttons.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Attendance;

